import {
    Box,
    Flex,
    Text,
    VStack,
    Badge,
    Button,
    HStack,
    IconButton,
    useToast,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    ModalCloseButton,
    Tooltip,
} from "@chakra-ui/react";
import { doc, updateDoc } from "firebase/firestore";
import { Trash2, MapPin, Truck, PackageCheck } from "lucide-react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { db } from "../../configs/firebase";
import { useAuth, UserAddress } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import { useState } from "react";

const cardsBorderRadii = "8px";

interface Props {
    addresses: UserAddress[];
    onSelectAddress?: (address: UserAddress | null) => void;
}

type ActionType =
    | "delivery"
    | "pickup"
    | "removeDelivery"
    | "removePickup"
    | "delete"
    | null;

const mapContainerStyle = {
    width: "100%",
    height: "100%",
};

const AddressList: React.FC<Props> = ({ addresses, onSelectAddress }) => {
    const { user, refreshUserSettings } = useAuth();
    const { theme } = useCustomTheme();
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [selectedAddress, setSelectedAddress] = useState<UserAddress | null>(null);
    const [actionType, setActionType] = useState<ActionType>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!user) return null;

    const openConfirmation = (address: UserAddress, action: ActionType) => {
        setSelectedAddress(address);
        setActionType(action);
        onOpen();
    };

    const closeConfirmation = () => {
        if (isSubmitting) return;
        setSelectedAddress(null);
        setActionType(null);
        onClose();
    };

    const setDelivery = async (address: UserAddress) => {
        try {
            const updated = addresses.map((a) => ({
                ...a,
                isCurrent: a.id === address.id,
            }));

            await updateDoc(doc(db, "users", user.uid), { addresses: updated });
            await refreshUserSettings();

            onSelectAddress?.(address);

            toast({
                title: "Delivery address updated",
                description: `"${address.label}" is now your current delivery address.`,
                status: "success",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
        } catch (error) {
            console.error("Failed to set delivery address:", error);
            toast({
                title: "Failed to update delivery address",
                description: "Please try again.",
                status: "error",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
            throw error;
        }
    };

    const setPickup = async (address: UserAddress) => {
        try {
            const updated = addresses.map((a) => ({
                ...a,
                isPickup: a.id === address.id,
            }));

            await updateDoc(doc(db, "users", user.uid), { addresses: updated });
            await refreshUserSettings();

            toast({
                title: "Pickup address updated",
                description: `"${address.label}" is now your current pickup address.`,
                status: "success",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
        } catch (error) {
            console.error("Failed to set pickup address:", error);
            toast({
                title: "Failed to update pickup address",
                description: "Please try again.",
                status: "error",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
            throw error;
        }
    };

    const removeDelivery = async (address: UserAddress) => {
        try {
            if (!address.isPickup) {
                toast({
                    title: "At least one mode is required",
                    description: "This address must remain active for delivery or pickup.",
                    status: "warning",
                    duration: 2500,
                    isClosable: true,
                    position: "bottom",
                });
                return;
            }

            const updated = addresses.map((a) => ({
                ...a,
                isCurrent: a.id === address.id ? false : a.isCurrent,
            }));

            await updateDoc(doc(db, "users", user.uid), { addresses: updated });
            await refreshUserSettings();

            onSelectAddress?.(updated.find((a) => a.isCurrent) || null);

            toast({
                title: "Delivery mode removed",
                description: `"${address.label}" will remain active for pickup only.`,
                status: "success",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
        } catch (error) {
            console.error("Failed to remove delivery mode:", error);
            toast({
                title: "Failed to update address",
                description: "Please try again.",
                status: "error",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
            throw error;
        }
    };

    const removePickup = async (address: UserAddress) => {
        try {
            if (!address.isCurrent) {
                toast({
                    title: "At least one mode is required",
                    description: "This address must remain active for delivery or pickup.",
                    status: "warning",
                    duration: 2500,
                    isClosable: true,
                    position: "bottom",
                });
                return;
            }

            const updated = addresses.map((a) => ({
                ...a,
                isPickup: a.id === address.id ? false : a.isPickup,
            }));

            await updateDoc(doc(db, "users", user.uid), { addresses: updated });
            await refreshUserSettings();

            toast({
                title: "Pickup mode removed",
                description: `"${address.label}" will remain active for delivery only.`,
                status: "success",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
        } catch (error) {
            console.error("Failed to remove pickup mode:", error);
            toast({
                title: "Failed to update address",
                description: "Please try again.",
                status: "error",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
            throw error;
        }
    };

    const deleteAddress = async (address: UserAddress) => {
        try {
            const updated = addresses.filter((a) => a.id !== address.id);

            await updateDoc(doc(db, "users", user.uid), { addresses: updated });
            await refreshUserSettings();

            if (address.isCurrent) {
                onSelectAddress?.(updated.find((a) => a.isCurrent) || updated[0] || null);
            }

            toast({
                title: "Address deleted",
                description: `"${address.label}" has been removed from your saved addresses.`,
                status: "success",
                duration: 2200,
                isClosable: true,
                position: "bottom",
            });
        } catch (error) {
            console.error("Failed to delete address:", error);
            toast({
                title: "Failed to delete address",
                description: "Please try again.",
                status: "error",
                duration: 2500,
                isClosable: true,
                position: "bottom",
            });
            throw error;
        }
    };

    const handleConfirmAction = async () => {
        if (!selectedAddress || !actionType) return;

        setIsSubmitting(true);

        try {
            if (actionType === "delivery") {
                await setDelivery(selectedAddress);
            } else if (actionType === "pickup") {
                await setPickup(selectedAddress);
            } else if (actionType === "removeDelivery") {
                await removeDelivery(selectedAddress);
            } else if (actionType === "removePickup") {
                await removePickup(selectedAddress);
            } else if (actionType === "delete") {
                await deleteAddress(selectedAddress);
            }

            closeConfirmation();
        } finally {
            setIsSubmitting(false);
        }
    };

    const getModalTitle = () => {
        if (actionType === "delivery") return "Confirm delivery address";
        if (actionType === "pickup") return "Confirm pickup address";
        if (actionType === "removeDelivery") return "Remove delivery mode";
        if (actionType === "removePickup") return "Remove pickup mode";
        if (actionType === "delete") return "Delete saved address";
        return "Confirm action";
    };

    const getModalMessage = () => {
        if (!selectedAddress) return "";

        if (actionType === "delivery") {
            return `Are you sure you want to make "${selectedAddress.label}" your current delivery address? Orders for delivery will use this location by default.`;
        }

        if (actionType === "pickup") {
            return `Are you sure you want to make "${selectedAddress.label}" your current pickup address? Pickup requests will use this location by default.`;
        }

        if (actionType === "removeDelivery") {
            return `Are you sure you want to remove delivery from "${selectedAddress.label}"? It will remain active for pickup.`;
        }

        if (actionType === "removePickup") {
            return `Are you sure you want to remove pickup from "${selectedAddress.label}"? It will remain active for delivery.`;
        }

        if (actionType === "delete") {
            return `Are you sure you want to remove "${selectedAddress.label}" from your saved addresses?`;
        }

        return "";
    };

    const getConfirmButtonText = () => {
        if (actionType === "delivery") return "Use for delivery";
        if (actionType === "pickup") return "Use for pickup";
        if (actionType === "removeDelivery") return "Remove delivery";
        if (actionType === "removePickup") return "Remove pickup";
        if (actionType === "delete") return "Delete address";
        return "Confirm";
    };

    const hasValidCoordinates =
        !!selectedAddress &&
        typeof selectedAddress.latitude === "number" &&
        typeof selectedAddress.longitude === "number" &&
        !Number.isNaN(selectedAddress.latitude) &&
        !Number.isNaN(selectedAddress.longitude);

    const isGoogleMapsReady =
        typeof window !== "undefined" &&
        !!(window as any).google &&
        !!(window as any).google.maps;

    return (
        <>
            <VStack spacing={2.5} align="stretch">
                {addresses.map((address) => {
                    const isDelivery = !!address.isCurrent;
                    const isPickup = !!address.isPickup;
                    const hasBothModes = isDelivery && isPickup;
                    const hasOnlyDelivery = isDelivery && !isPickup;
                    const hasOnlyPickup = !isDelivery && isPickup;

                    const itemBorderColor = isDelivery
                        ? theme.brand?.electricBlue || theme.primary
                        : isPickup
                            ? "purple.400"
                            : theme.border;

                    const statusLabel = isDelivery
                        ? isPickup
                            ? "Delivery & Pickup"
                            : "Delivery"
                        : isPickup
                            ? "Pickup"
                            : "Saved";

                    const statusScheme = isDelivery
                        ? "teal"
                        : isPickup
                            ? "purple"
                            : "gray";

                    const deliveryActionType: ActionType = isDelivery
                        ? "removeDelivery"
                        : "delivery";

                    const pickupActionType: ActionType = isPickup
                        ? "removePickup"
                        : "pickup";

                    const isDeliveryButtonDisabled = hasOnlyDelivery;
                    const isPickupButtonDisabled = hasOnlyPickup;

                    const deliveryTooltipText = hasBothModes
                        ? "This address is active for delivery and pickup. Click to turn off delivery and keep pickup active."
                        : hasOnlyDelivery
                            ? "Delivery is the only active mode for this address. At least one mode must remain active."
                            : "This will make this saved address your current delivery address. Would you like to change it?";

                    const pickupTooltipText = hasBothModes
                        ? "This address is active for delivery and pickup. Click to turn off pickup and keep delivery active."
                        : hasOnlyPickup
                            ? "Pickup is the only active mode for this address. At least one mode must remain active."
                            : "This will make this saved address your current pickup address. Would you like to change it?";

                    return (
                        <Box
                            key={address.id}
                            px={{ base: 3, md: 3.5 }}
                            py={{ base: 3, md: 3 }}
                            border="1px solid"
                            borderColor={itemBorderColor}
                            borderRadius={cardsBorderRadii}
                            bg={theme.surface}
                            boxShadow={isDelivery || isPickup ? theme.shadowSm : "none"}
                            transition="all 0.18s ease"
                            _hover={{
                                borderColor: itemBorderColor,
                                boxShadow: theme.shadowSm,
                                transform: "translateY(-1px)",
                            }}
                        >
                            <Flex
                                align={{ base: "flex-start", md: "center" }}
                                justify="space-between"
                                gap={3}
                                direction={{ base: "column", md: "row" }}
                            >
                                <HStack align="flex-start" spacing={3} flex="1" minW={0} w="100%">
                                    <Flex
                                        w="34px"
                                        h="34px"
                                        minW="34px"
                                        borderRadius={cardsBorderRadii}
                                        align="center"
                                        justify="center"
                                        bg={theme.surfaceAlt}
                                        color={
                                            isDelivery
                                                ? theme.primary
                                                : isPickup
                                                    ? "purple.400"
                                                    : theme.textSubtle
                                        }
                                        border="1px solid"
                                        borderColor={theme.border}
                                    >
                                        <MapPin size={16} />
                                    </Flex>

                                    <Box flex="1" minW={0}>
                                        <Flex align="center" gap={2} wrap="wrap">
                                            <Text
                                                fontWeight="700"
                                                color={theme.text}
                                                fontSize="sm"
                                                noOfLines={1}
                                            >
                                                {address.label}
                                            </Text>

                                            <Badge
                                                colorScheme={statusScheme}
                                                borderRadius={cardsBorderRadii}
                                                px={2}
                                                py={0.5}
                                                fontSize="10px"
                                            >
                                                {statusLabel}
                                            </Badge>
                                        </Flex>

                                        <Text
                                            fontSize="xs"
                                            color={theme.textSubtle}
                                            mt={0.5}
                                            noOfLines={2}
                                            lineHeight="1.35"
                                        >
                                            {address.addressLine}
                                        </Text>
                                    </Box>
                                </HStack>

                                <Flex
                                    align="center"
                                    gap={2}
                                    w={{ base: "100%", md: "auto" }}
                                    justify={{ base: "space-between", md: "flex-end" }}
                                    flexShrink={0}
                                >
                                    <HStack spacing={1.5}>
                                        <Tooltip
                                            label={deliveryTooltipText}
                                            hasArrow
                                            placement="top"
                                            openDelay={350}
                                            borderRadius={cardsBorderRadii}
                                            fontSize="xs"
                                        >
                                            <Button
                                                size="xs"
                                                h="30px"
                                                px={3}
                                                leftIcon={<Truck size={13} />}
                                                variant={isDelivery ? "solid" : "outline"}
                                                onClick={() => openConfirmation(address, deliveryActionType)}
                                                isDisabled={isDeliveryButtonDisabled}
                                                color={isDelivery ? theme.textOnPrimary : theme.text}
                                                bgImage={isDelivery ? theme.gradients.primaryButton : undefined}
                                                bg={isDelivery ? undefined : "transparent"}
                                                borderColor={theme.borderStrong}
                                                borderRadius={cardsBorderRadii}
                                                fontSize="11px"
                                                maxW={{ base: "145px", md: "135px" }}
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                _hover={{
                                                    bgImage: isDelivery
                                                        ? theme.gradients.primaryButtonHover
                                                        : undefined,
                                                    bg: !isDelivery ? theme.hover : undefined,
                                                }}
                                            >
                                                {hasBothModes
                                                    ? "Remove delivery"
                                                    : isDelivery
                                                        ? "Delivery active"
                                                        : "Use delivery"}
                                            </Button>
                                        </Tooltip>

                                        <Tooltip
                                            label={pickupTooltipText}
                                            hasArrow
                                            placement="top"
                                            openDelay={350}
                                            borderRadius={cardsBorderRadii}
                                            fontSize="xs"
                                        >
                                            <Button
                                                size="xs"
                                                h="30px"
                                                px={3}
                                                leftIcon={<PackageCheck size={13} />}
                                                colorScheme="purple"
                                                variant={isPickup ? "solid" : "outline"}
                                                onClick={() => openConfirmation(address, pickupActionType)}
                                                isDisabled={isPickupButtonDisabled}
                                                borderColor={!isPickup ? theme.borderStrong : undefined}
                                                color={!isPickup ? theme.text : undefined}
                                                borderRadius={cardsBorderRadii}
                                                fontSize="11px"
                                                maxW={{ base: "135px", md: "125px" }}
                                                whiteSpace="nowrap"
                                                overflow="hidden"
                                                textOverflow="ellipsis"
                                                _hover={!isPickup ? { bg: theme.hover } : undefined}
                                            >
                                                {hasBothModes
                                                    ? "Remove pickup"
                                                    : isPickup
                                                        ? "Pickup active"
                                                        : "Use pickup"}
                                            </Button>
                                        </Tooltip>
                                    </HStack>

                                    <IconButton
                                        aria-label="Delete address"
                                        icon={<Trash2 size={14} />}
                                        size="xs"
                                        h="30px"
                                        w="30px"
                                        minW="30px"
                                        variant="ghost"
                                        borderRadius={cardsBorderRadii}
                                        color={theme.danger || "red.500"}
                                        onClick={() => openConfirmation(address, "delete")}
                                        _hover={{
                                            bg: theme.hover,
                                            color: theme.danger || "red.600",
                                        }}
                                    />
                                </Flex>
                            </Flex>
                        </Box>
                    );
                })}
            </VStack>

            <Modal isOpen={isOpen} onClose={closeConfirmation} isCentered size="md">
                <ModalOverlay />
                <ModalContent borderRadius={cardsBorderRadii} bg={theme.surface} overflow="hidden">
                    <ModalHeader color={theme.text} pb={2}>
                        {getModalTitle()}
                    </ModalHeader>

                    <ModalCloseButton color={theme.text} isDisabled={isSubmitting} />

                    <ModalBody pb={4}>
                        <VStack align="stretch" spacing={3}>
                            <HStack
                                align="flex-start"
                                spacing={3}
                                p={3}
                                borderRadius={cardsBorderRadii}
                                bg={theme.surfaceAlt}
                                border="1px solid"
                                borderColor={theme.border}
                            >
                                <Flex
                                    w="34px"
                                    h="34px"
                                    minW="34px"
                                    align="center"
                                    justify="center"
                                    borderRadius={cardsBorderRadii}
                                    bg={theme.hover}
                                    color={theme.primary}
                                >
                                    <MapPin size={16} />
                                </Flex>

                                <Box flex="1" minW={0}>
                                    <Text fontWeight="700" color={theme.text} fontSize="sm">
                                        {selectedAddress?.label}
                                    </Text>

                                    <Text
                                        fontSize="xs"
                                        color={theme.textSubtle}
                                        mt={0.5}
                                        noOfLines={2}
                                        lineHeight="1.35"
                                    >
                                        {selectedAddress?.addressLine}
                                    </Text>
                                </Box>
                            </HStack>

                            {hasValidCoordinates && (
                                <Box
                                    h="150px"
                                    borderRadius={cardsBorderRadii}
                                    overflow="hidden"
                                    border="1px solid"
                                    borderColor={theme.border}
                                    bg={theme.surfaceAlt}
                                >
                                    {isGoogleMapsReady ? (
                                        <GoogleMap
                                            mapContainerStyle={mapContainerStyle}
                                            center={{
                                                lat: selectedAddress.latitude,
                                                lng: selectedAddress.longitude,
                                            }}
                                            zoom={16}
                                            options={{
                                                streetViewControl: false,
                                                mapTypeControl: false,
                                                fullscreenControl: false,
                                                zoomControl: true,
                                            }}
                                        >
                                            <Marker
                                                position={{
                                                    lat: selectedAddress.latitude,
                                                    lng: selectedAddress.longitude,
                                                }}
                                            />
                                        </GoogleMap>
                                    ) : (
                                        <Flex
                                            h="100%"
                                            align="center"
                                            justify="center"
                                            px={4}
                                        >
                                            <Text fontSize="sm" color={theme.textSubtle}>
                                                Map loading or unavailable right now.
                                            </Text>
                                        </Flex>
                                    )}
                                </Box>
                            )}

                            <Text color={theme.textSubtle} fontSize="sm" lineHeight="1.5">
                                {getModalMessage()}
                            </Text>
                        </VStack>
                    </ModalBody>

                    <ModalFooter gap={2} pt={0}>
                        <Button
                            variant="outline"
                            onClick={closeConfirmation}
                            isDisabled={isSubmitting}
                            size="sm"
                            minW="110px"
                            borderRadius={cardsBorderRadii}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={handleConfirmAction}
                            isLoading={isSubmitting}
                            size="sm"
                            minW="130px"
                            borderRadius={cardsBorderRadii}
                            color={actionType === "delete" ? "white" : theme.textOnPrimary}
                            bg={actionType === "delete" ? theme.danger || "red.500" : undefined}
                            bgImage={
                                actionType !== "delete"
                                    ? theme.gradients.primaryButton
                                    : undefined
                            }
                            _hover={{
                                bg:
                                    actionType === "delete"
                                        ? theme.danger || "red.600"
                                        : undefined,
                                bgImage:
                                    actionType !== "delete"
                                        ? theme.gradients.primaryButtonHover
                                        : undefined,
                            }}
                        >
                            {getConfirmButtonText()}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default AddressList;